import React from "react";

import { BrandingUtilities } from "../../styles/branding-utilities";
import { THEME_COLORS } from "../../theme/themeTokens";

import { BADGE_ICONS } from "./badgeIcons";
import { DEPRECATED_ICONS } from "./IconMap";
import { IconTypes } from "./IconTypes";
import { IconDictionary } from "./types";

export const SIZES = {
  ExtraSmall: 8,
  Small: 12,
  Medium: 16,
  Large: 24,
};

export const VARIANTS = {
  regular: "regular",
  filled: "filled",
  thin: "thin",
};

export interface IconProps {
  /**
   * Which icon to render. Note: the awkward type here allows for accepting a
   * string (for legacy support) WITH helpful autocomplete of valid icons.
   */
  icon: IconDictionary;
  /**
   * Class name to apply to the icon box.
   */
  className?: string;
  /**
   * Test ID for the icon, for testing purposes
   */
  "data-testid"?: string;
  /**
   * Manual color to apply.
   * @deprecated Don't use this. Style with `currentColor` where possible
   */
  color?: string;
  /**
   * Size (square) of the icon. Suggested values are: 8, 12, 16, 24
   *
   * Default: `16`
   */
  size?: number;
  /**
   * The variant of the icon. If the variant for that icon does not exist,
   * icons will render the regular variant by default.
   *
   * Default: `regular`
   */
  variant?: "regular" | "filled" | "thin";
  /**
   * If `true`, renders the icon as inert (non-focusable, non-interactive).
   */
  decorative?: boolean;
  /**
   * The pointerEvents property to pass to the icon style.
   */
  pointerEvents?:
    | "auto"
    | "none"
    | "visiblePainted"
    | "visibleFill"
    | "visibleStroke"
    | "visible"
    | "painted"
    | "fill"
    | "stroke"
    | "all"
    | "inherit"
    | "initial"
    | "revert"
    | "revert"
    | "unset";
}

/**
 * An component that renders an SVG icon from IconDictionary.
 */
export function Icon({
  icon,
  className = "",
  "data-testid": dataTestId,
  color = THEME_COLORS.textPrimary,
  size = 16,
  variant = "regular",
  decorative = false,
  pointerEvents,
}: IconProps) {
  const sizeStyle = {
    width: size,
    height: size,
  };

  const viewBox = getViewbox(icon);
  const classes = className.split(" ");
  const decorativeProps = decorative
    ? { "aria-hidden": true, role: "none", focusable: false }
    : {};

  let iconColor = "currentColor";

  React.useEffect(() => {
    if (DEPRECATED_ICONS.includes(icon)) {
      // the icon is deprecated and should be removed for another icon so we can completely remove all from code.
      // eslint-disable-next-line no-console
      console.warn(
        `The following icons in use are now deprecated: ${JSON.stringify(
          icon
        )}. Please use an alternative.`
      );
    }
  }, []);

  if (BrandingUtilities.hasBrandingClass(classes)) {
    if (color != THEME_COLORS.textPrimary) {
      // the color value is set to the non default and we have a branding css value,
      // so we will ignore the color argument.
      // we have to continue supporting a default color for backwards-compatibility reasons.
      // eslint-disable-next-line no-console
      console.warn(
        `You have both specified a branding css class and set the color value to ${color}. Ignoring the color value of ${color}.`
      );
    }
  } else {
    // ok, use the color value they passed in
    iconColor = color;
  }

  return (
    <svg
      viewBox={viewBox}
      className={className}
      style={sizeStyle}
      data-testid={`${dataTestId || "icon"}-svg`}
      {...(pointerEvents ? { pointerEvents } : {})}
      {...decorativeProps}
    >
      {IconTypes[variant](iconColor, icon)}
    </svg>
  );
}

// These are icons that used a weird method of rendering. They are, for whatever
// reason, sized for a smaller viewbox than anything else. Ideally, we want to
// replace these one-offs and odd viewboxes to icons that are sized appropriately
// for the rest of the icons.

const EXTRA_LARGE_VIEWBOX_ICONS = [
  "pinned_icon_award",
  "pinned_icon_bright_idea",
  "pinned_icon_bullseye",
  "pinned_icon_champagne",
  "pinned_icon_cheer",
  "pinned_icon_crown",
  "pinned_icon_diamond",
  "pinned_icon_hole_in_one",
  "pinned_icon_medal",
  "pinned_icon_metrics",
  "pinned_icon_pin",
  "pinned_icon_rock_and_roll",
  "pinned_icon_rocket",
  "pinned_icon_strength",
  "pinned_icon_teddy_bear",
  "quotes_left",
  "quotes_right",
];

const LARGE_VIEWBOX_ICONS = ["image_file", "cloud_download"];

const SMALL_VIEWBOX_ICONS = [
  "dot",
  "progress_circle",
  "caret_down",
  "up_down_caret",
  "nomination",
];

const EXTRA_SMALL_VIEWBOX_ICONS = ["private_push_pin"];

const VIEWBOX_SIZES = {
  extraLarge: `0 0 48 48`,
  large: `0 0 40 40`,
  default: "0 0 24 24",
  small: "0 0 18 18",
  extraSmall: "0 0 13 13",
};

const CUSTOM_VIEWBOX_SIZES: Record<string, string> = {
  media_error: "0 0 16 16",
  hand_expanded: "0 0 17 15",
  bubble_graph: "0 0 64 64",
  infinite: "0 0 12 7",
  trophy: "0 0 16 16",
  qr_code: "0 0 16 16",
};

function getViewbox(icon: string) {
  if (icon in CUSTOM_VIEWBOX_SIZES) {
    return CUSTOM_VIEWBOX_SIZES[icon];
  }

  // Badge icons are authored on a 64x64 canvas to match the badge artwork.
  if (BADGE_ICONS.some((name) => name === icon)) {
    return "0 0 64 64";
  }

  if (EXTRA_LARGE_VIEWBOX_ICONS.includes(icon)) {
    return VIEWBOX_SIZES.extraLarge;
  }

  if (LARGE_VIEWBOX_ICONS.includes(icon)) {
    return VIEWBOX_SIZES.large;
  }

  if (SMALL_VIEWBOX_ICONS.includes(icon)) {
    return VIEWBOX_SIZES.small;
  }

  if (EXTRA_SMALL_VIEWBOX_ICONS.includes(icon)) {
    return VIEWBOX_SIZES.extraSmall;
  }

  return VIEWBOX_SIZES.default;
}
